import React from 'react'
import PropTypes from 'prop-types'
import injectJss from 'react-jss'
import compose from 'recompose/compose'
import pure from 'recompose/pure'
import lifecycle from 'recompose/lifecycle'
import classNames from 'classnames'
import { Icon } from 'aria-viewer/common/components/Icon'
import ListItem from '../../../containers/elements_list/ListItem'
import CHECK_CIRCLE from 'public/assets/svg/check_circle.svg'

const style = theme => ({
    container: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.common.white,
        cursor: 'pointer',
        width: '56px',
        height: '56px',
        display: ({ displayElementList }) => (displayElementList ? 'block' : 'none'),
        borderRadius: '50%', // transitions are in interactiveElementsListContainer
        '&.expanded': {
            width: '310px',
            height: ({ elements }) => (elements.length > 3 ? '270.5px' : 60 + elements.length * 60 + 'px'),
            borderRadius: '0%',
            [theme.breakpoints.down('xs')]: {
                width: '100%',
                height: '100% !important',
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                overflowY: 'scroll',
            },
        },
    },
    header: {
        width: '56px',
        height: '56px',
        boxSizing: 'border-box',
        textAlign: 'center',
        padding: ({ numDigits }) => {
            const digits = numDigits - 2

            if (digits >= 2) return '18px 2px'
            else if (digits >= 0 && digits < 2) return '15px 12px'

            return '15px 8px'
        },
        fontSize: ({ numDigits }) => 20 - numDigits + 2,
        '& span.visited': {
            transition: ({ isDeviceMobile }) => (isDeviceMobile ? 'all 0.7s ease-out' : 'all 0.2s ease-out'),
            transitionDelay: '0.2s',
        },
        '& span.total': {
            marginLeft: '0',
        },
        '& span.title1': {
            display: 'none',
        },
        '&.unchecked .checkedIcon': {
            display: 'none',
        },
        '&.checked .checkedIcon': {
            display: 'block',
            position: 'absolute',
            top: '5px',
            right: '-10px',
        },
        '&.expanded': {
            width: '310px',
            height: '60px',
            padding: '0px 12px 4px 12px',
            borderRadius: '0%',
            '& span.visited': {
                fontWeight: 'medium',
                fontSize: ({ numDigits }) => 48 - numDigits + 2 + 'px',
            },
            '& span.slash': {
                fontSize: '18px',

                margin: '0 4px',
            },
            '& span.total': {
                fontSize: ({ numDigits }) => 21 - numDigits + 2 + 'px',
                marginRight: '16px',
            },
            '& .checkedIcon': {
                display: 'none',
            },
            '& span.title1': {
                display: 'inline',
                opacity: '1',
                fontSize: ({ numDigits }) => 15 - numDigits + 2 + 'px',
            },
            [theme.breakpoints.down('xs')]: {
                width: '100%',
                '& span.title1': {
                    fontSize: '19px',
                },
            },
        },
    },
    divider: {
        height: '1px',
        backgroundColor: theme.palette.common.white,
        margin: '0 20px',
        '&.minimized': {
            display: 'none',
        },
        // mobile
        [theme.breakpoints.down('xs')]: {
            backgroundColor: '#bbbbbb',
        },
    },
    list: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        opacity: 0,
        height: ({ isDeviceMobile, elements }) =>
            isDeviceMobile ? '' : elements.length > 3 ? '210.5px' : elements.length * 60 + 'px',
        maxHeight: ({ isDeviceMobile }) => (isDeviceMobile ? '' : '210.5px'),
        overflowY: 'scroll',
        transition: ({ isDeviceMobile }) => (isDeviceMobile ? 'all 3s' : 'all 1s'),
        transitionDelay: ({ isDeviceMobile }) => (isDeviceMobile ? 'all 1s' : 'all 2s'),
        '&.expanded': {
            opacity: 1,
        },
        [theme.breakpoints.down('xs')]: {
            maxHeight: 'initial',
        },
    },
    closeButton: {
        display: 'none',
        position: 'absolute',
        top: '8px',
        right: '8px',
        fontSize: '24px',
        '&.expanded': {
            [theme.breakpoints.down('xs')]: {
                display: 'initial',
            },
        },
    },
})

const InteractiveElementsList = ({
    className,
    classes,
    elements,
    currentState,
    onMouseEnter,
    onMouseLeave,
    onToggleList,
    handleListItemClick,
    visitedElementCount,
    elementCount,
    stopId,
    ...props
}) => (
    <div
        className={classNames(className, classes.container, currentState)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onToggleList}
    >
        <div className={classNames(classes.closeButton, currentState)}>
            <Icon>clear</Icon>
        </div>
        <div className={classNames(classes.header, currentState, visitedElementCount > 0 ? 'checked' : 'unchecked')}>
            <img className="checkedIcon" src={CHECK_CIRCLE} />
            <span className="visited">{visitedElementCount}</span>
            <span className="slash">/</span>
            <span className="total">{elementCount} </span>
            <span className="title1">interactions explored</span>
        </div>
        <div className={classNames(classes.divider, currentState)} />
        <ul className={classNames(classes.list, currentState)}>
            {currentState === 'expanded' &&
                elements.map(({ type, id }, i) => <ListItem key={i} {...{ type, id, stopId }} />)}
        </ul>
    </div>
)

export default compose(
    lifecycle({
        componentDidMount: function() {
            this.props.onComponentDidMount()
        },
    }),
    injectJss(style),
    pure
)(InteractiveElementsList)
